import { memo } from 'react';
import {
  useHasCashflow,
  useHasOutstanding,
  useHasSeeInvoices,
  useHasTreasury,
  useIsBankAccountLeadSource,
  useIsPayrollConsultant,
  useShouldShowAiChatSection,
} from '~/hooks/useFeatureFlag';
import { useUserBankAccountPermissions } from '~/hooks/useUserBankAccountPermissions';
import { UserRole } from '~/types/company';
import styles from './NavigationSidebarContent.module.scss';
import AiChatItem from './navigationSidebarItems/AiChatItem';
import AnalyticsItem from './navigationSidebarItems/AnalyticsItem';
import BanksItem from './navigationSidebarItems/BanksItem';
import BookkeepingItem from './navigationSidebarItems/BookkeepingItem';
import CashflowItem from './navigationSidebarItems/CashflowItem';
import InvoicesItem from './navigationSidebarItems/InvoicesItem';
import OutstandingItem from './navigationSidebarItems/OutstandingItem';
import ReferralItem from './navigationSidebarItems/ReferralItem';
import SettingsItem from './navigationSidebarItems/SettingstItem';
import { SidebarCompany } from './navigationSidebarItems/SidebarCompany';
import SidebarLogo from './navigationSidebarItems/SidebarLogo';
import SupportItem from './navigationSidebarItems/SupportItem';

interface NavigationSidebarContentProps {
  isCompanyAccessible: boolean;
}

const NavigationSidebarContent: React.FC<NavigationSidebarContentProps> = ({ isCompanyAccessible }) => {
  const hasTreasury = useHasTreasury([UserRole.ENTREPRENEUR]);
  const hasCashflow = useHasCashflow([UserRole.ENTREPRENEUR]);
  const hasOutstanding = useHasOutstanding([UserRole.ENTREPRENEUR]);
  const hasSeeInvoices = useHasSeeInvoices([UserRole.ENTREPRENEUR]);
  const shouldShowAiChatSection = useShouldShowAiChatSection();
  const isPayrollConsultant = useIsPayrollConsultant();

  const isBankAccountLeadSource = useIsBankAccountLeadSource();
  const { isSibillAccountEnabled } = useUserBankAccountPermissions();

  const showCashflowItem = !isBankAccountLeadSource && hasCashflow;
  const showOutstandingItem = !isBankAccountLeadSource && hasOutstanding;
  const showInvoicesItem = !isBankAccountLeadSource && hasSeeInvoices;

  return (
    <div className={styles.innerContainer}>
      <div className={styles.filler}></div>
      <div>
        <SidebarLogo />

        <SidebarCompany />
        {isCompanyAccessible &&
          (isPayrollConsultant ? (
            <BookkeepingItem />
          ) : (
            <>
              {(hasTreasury || isSibillAccountEnabled) && <BanksItem />}
              {showCashflowItem && <CashflowItem />}
              {showOutstandingItem && <OutstandingItem />}
              {showInvoicesItem && <InvoicesItem />}
              <BookkeepingItem />
              <AnalyticsItem />
              {shouldShowAiChatSection && <AiChatItem />}
            </>
          ))}
      </div>
      <div className={styles.supportSettingsContainer}>
        <ReferralItem />
        <SupportItem />
        <SettingsItem isCompanyAccessible={isCompanyAccessible} />
      </div>
    </div>
  );
};

export default memo(NavigationSidebarContent);
